/**
 * Which `TickDerived` layer (derived.ts) belongs to a state while `tick()`
 * runs. Kept apart from derived.ts so the code that moves units
 * (occupancy.ts `deployUnit`) can invalidate it without an import cycle.
 */
import type { TickDerived } from './derived';
import type { SimState } from './types';

const active = new WeakMap<SimState, TickDerived>();

/** The layer `tick()` registered for `state`, if one is running. */
function activeDerived(state: SimState): TickDerived | undefined {
  return active.get(state);
}

function registerDerived(state: SimState, derived: TickDerived): void {
  active.set(state, derived);
}

function unregisterDerived(state: SimState): void {
  active.delete(state);
}

/**
 * Drops everything the running tick's layer has worked out for `state`.
 * Called wherever units move or appear and disappear, or buildings change,
 * mid-tick (movement, deploys, deaths, new or finished buildings). A no-op
 * outside `tick()`.
 */
function invalidateDerived(state: SimState): void {
  active.get(state)?.invalidate();
}

/**
 * Like `invalidateDerived`, once the running system ends (derived.ts
 * `invalidateAfterSystem`). A no-op outside `tick()`.
 */
function invalidateDerivedAfterSystem(state: SimState): void {
  active.get(state)?.invalidateAfterSystem();
}

export {
  activeDerived,
  invalidateDerived,
  invalidateDerivedAfterSystem,
  registerDerived,
  unregisterDerived,
};
